import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ShopLinkRoutes } from './app/routes';
import { UpsellModal } from './app/ProductDetail/UpsellModal';
import { ToastHost } from './toast/ToastHost';
import { AuthProvider } from './auth/AuthProvider';
import { BrandThemeApplier } from './theme/BrandThemeApplier';
import { startFavouritesHydrationOnAuth } from './favourites/hydrateFavouritesOnAuth';
import { startCatalogueEntryResets } from './ordering/catalogueEntryResets';
import { startHhtOrdersLoadOnAuth } from './hht/loadHhtOrdersOnAuth';
import { HhtWaitingCountRefresher } from './hht/HhtWaitingCountRefresher';
import { ConflictsClearedOnLeave } from './basket/ConflictsClearedOnLeave';
import './styles.css';

// Story 7-FU-D (ADO-1217), AC4 — hydrate favourites once when the session becomes
// authenticated, so per-card hearts on /products reflect real state on a fresh
// reload (not empty-until-/favourites). Wired at the composition root so no
// listing screen's tests are coupled to the favourites endpoint.
startFavouritesHydrationOnAuth();
// Enabler E7-C (ADO-2274), D-1: the catalogue caches hold one ordering entry's answers; a change
// of entry resets them and re-reads favourites.
startCatalogueEntryResets();

// Story 8.10 (ADO-2019), AC1 / AC2 / DV-39: read the HHT waiting count once at sign-in, for
// the header's "HHT Orders" link and waiting badge. Never blocks sign-in or the depot gate.
startHhtOrdersLoadOnAuth();

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element #root not found in index.html');
}

const root = createRoot(rootElement);

// Story 6.34 (ADO-1344) — the route TABLE moved to the side-effect-free
// `app/routes.tsx` so it can be rendered under `MemoryRouter` in a spec; this
// module keeps only the `createRoot` side effect, which makes it un-importable
// from a test. Everything left here is composition, not routing.
root.render(
  <StrictMode>
    <BrowserRouter>
      {/* AuthProvider is a plain bootstrap component (not a Context provider);
          it resolves Auth0 config and hydrates the Zustand auth store (AC2/AC4). */}
      <AuthProvider>
        {/* Story 6.35 (ADO-1693) — apply the resolved tenant brand to the
            document root (data-brand + chrome-colour overrides). Side-effect
            only; the catalogue and shell re-theme through tokens.css. */}
        <BrandThemeApplier />
        <ShopLinkRoutes />
        {/* Story 7.12 Iteration 2 (ADO-911), AC7 — the Linked Product upsell HOST
            is mounted ONCE at the composition root, OUTSIDE the route table, so
            EVERY add-to-basket entry point on EVERY screen (PDP hero, quick-add,
            listing card, search, substitutes row, carousel tile) opens the same
            single upsell. It renders nothing until `openUpsell` opens it, then —
            per the 2026-08-14 design refresh — dispatches to the full MODAL (a PDP
            add) or the non-invasive corner TOAST (a search/listing add), keyed on
            the add's origin. It stays outside `ShopLinkRoutes` deliberately: it is
            global chrome rather than a route, and must survive navigation. */}
        <UpsellModal />
        {/* Story 8.7 (ADO-2016) — the single toast host, global chrome like the upsell. */}
        <ToastHost />
        {/* Story 8.10 (ADO-2019), DV-39: re-read the HHT waiting count on route changes (at
            most once a minute) and when the tab regains focus. Renders nothing. */}
        <HhtWaitingCountRefresher />
        <ConflictsClearedOnLeave />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
